/**
 * The live layer (client side) — quiet refresh for every active, logged-in user.
 *
 * APPROVED DESIGN, 30 Oct (item #1), Option A: no socket, no new table. While a
 * screen is open and visible, this polls /api/live/markers about every ten
 * seconds. Each marker is a short signature of a domain (orders, payments,
 * deliveries, …); when one changes, the matching domain's revision counter
 * bumps, and any query that asked to watch that domain re-fetches itself. So a
 * salesman's order shows up on the admin's screen within ~10s, with no banners
 * and no taps — a quiet refresh, which is what was chosen.
 *
 * Only an open, visible, signed-in app polls, so "active and logged in" needs
 * no presence table — a closed or backgrounded app simply asks nothing.
 */

import {
  createContext, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { api } from './api';
import { useAuth } from './auth';

type Markers = Record<string, string>;

interface LiveState {
  /** One counter per domain; it increments each time that domain's marker moves. */
  revisions: Record<string, number>;
}

const LiveContext = createContext<LiveState>({ revisions: {} });

const POLL_MS = 10000;

export function LiveProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [revisions, setRevisions] = useState<Record<string, number>>({});
  const prev = useRef<Markers | null>(null);

  useEffect(() => {
    // Signed out: stop, and drop the baseline so signing back in starts clean.
    if (!user) { prev.current = null; return; }

    let cancelled = false;

    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await api.get<{ markers: Markers }>('/api/live/markers');
        if (cancelled) return;
        const next = res.markers ?? {};
        const before = prev.current;
        prev.current = next;
        // The FIRST read only establishes the baseline — it must bump nothing,
        // or every screen would refetch once the moment it mounted.
        if (!before) return;
        const changed = Object.keys(next).filter((k) => next[k] !== before[k]);
        if (changed.length) {
          setRevisions((r) => {
            const copy = { ...r };
            for (const k of changed) copy[k] = (copy[k] ?? 0) + 1;
            return copy;
          });
        }
      } catch {
        // A failed poll (offline, token refreshing) just waits for the next tick.
      }
    };

    void tick(); // establish the baseline immediately
    const timer = window.setInterval(() => { void tick(); }, POLL_MS);
    // Refresh the instant the tab becomes visible again, rather than waiting out
    // the interval — matches how the rest of the app behaves on focus.
    const onVis = () => { if (document.visibilityState === 'visible') void tick(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user]);

  return <LiveContext.Provider value={{ revisions }}>{children}</LiveContext.Provider>;
}

/**
 * A number that increments whenever any of the named domains change.
 *
 * Feed it into a query's deps (useQuery does this via its `refreshOn` option)
 * and the query re-fetches on the next live change to those domains. With no
 * provider mounted, or no domains named, it is a constant 0 — so a screen that
 * does not opt in is completely unaffected.
 */
export function useLiveRevision(types?: string[]): number {
  const { revisions } = useContext(LiveContext);
  if (!types || types.length === 0) return 0;
  let sum = 0;
  for (const t of types) sum += revisions[t] ?? 0;
  return sum;
}
